import React, { useState } from 'react';
import {
  BugIcon,
  Button,
  DevicesOtherIcon,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Divider,
  SecurityIcon,
  Text,
} from '@billda/design-system';
import { DebugButton } from '../../../shared/components/dev/DebugButton';
import { FeatureSwitchGate } from '../../../shared/components/gates/FeatureSwitchGate';
import { Features } from '@billda/features-ui';
import { PermissionGate } from '../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { useAuthStore } from '../../../features/auth/stores/authStore';
import { useNavigate } from 'react-router-dom';
import { Routes as AppRoutes } from '../../../routes';

/**
 * Security settings area: sign out all devices and developer tools.
 */
export const SecurityArea: React.FC = () => {
  const { logoutAll } = useAuthStore();
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleLogoutAllDevices = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmLogoutAll = () => {
    setConfirmDialogOpen(false);
    console.log('👤 User initiated logout all from Settings page');
    navigate(AppRoutes.login);
    logoutAll('manual_user_action_settings');
  };

  const handleCancelLogoutAll = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <>
      {/* Security Section */}
      <div className="mb-6 rounded-xl border border-border-default bg-surface-default p-6">
        <div className="mb-4 flex items-center">
          <SecurityIcon
            aria-hidden="true"
            className="mr-2 size-5 text-content-accent"
          />
          <Text variant="h6">Security</Text>
        </div>

        <Text as="div" variant="body2" color="secondary" className="mb-6">
          Manage your account security settings.
        </Text>

        <Divider className="mb-6" />

        <div className="flex flex-col gap-6">
          <div>
            <Text as="div" variant="body1" className="mb-2 font-medium">
              Sign Out All Devices
            </Text>
            <Text as="div" variant="body2" color="secondary" className="mb-4">
              Sign out from all browsers and devices. You will need to sign in
              again on each device.
            </Text>
            <Button
              variant="outlineDanger"
              onClick={handleLogoutAllDevices}
              data-testid="settings-logout-all-button"
            >
              <DevicesOtherIcon aria-hidden="true" className="size-4" />
              Sign out all devices
            </Button>
          </div>
        </div>
      </div>

      {/* Confirm Logout All Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            handleCancelLogoutAll();
          }
        }}
      >
        <DialogContent data-testid="confirm-logout-all-dialog">
          <DialogHeader>Sign Out All Devices</DialogHeader>
          <DialogBody>
            This will sign you out from all browsers and devices, including this
            one. You will need to sign in again.
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={handleCancelLogoutAll}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmLogoutAll}
              data-testid="confirm-logout-all-button"
            >
              Sign Out All Devices
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Developer Tools (permission + feature-gated) */}
      <PermissionGate
        permission={Permissions.Developer.Access}
        showRequiredPermissions={false}
      >
        <FeatureSwitchGate feature={Features.Debug.IsPanelEnabled}>
          <div className="mb-6 rounded-xl border border-border-default bg-surface-default p-6">
            <div className="mb-4 flex items-center">
              <BugIcon
                aria-hidden="true"
                className="mr-2 size-5 text-content-accent"
              />
              <Text variant="h6">Developer Tools</Text>
            </div>

            <Text as="div" variant="body2" color="secondary" className="mb-6">
              Tools and utilities for debugging and development.
            </Text>

            <Divider className="mb-6" />

            <div className="flex flex-col gap-6">
              <div>
                <Text as="div" variant="body1" className="mb-2 font-medium">
                  Debug Panel
                </Text>
                <Text
                  as="div"
                  variant="body2"
                  color="secondary"
                  className="mb-4"
                >
                  Open the debug panel to inspect application state, JWT tokens,
                  and user data.
                </Text>
                <DebugButton size="medium" />
              </div>
            </div>
          </div>
        </FeatureSwitchGate>
      </PermissionGate>
    </>
  );
};
